import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import DashboardLayout from '../components/DashboardLayout';
import {
  clearProfileLookupsCache,
  fetchProfileLookups,
  formatProdi,
  hydrateProfile,
  hydrateProfiles,
} from '../lib/profileLookups';
import { fetchKriteriaList, mapCriteriaForAccess } from '../lib/kriteriaMetadata';
import { formatRoleLabel } from '../lib/accessRules';

const BASE_ROLES = ['superadmin', 'admin_prodi'];
const isManagedRole = (role) => BASE_ROLES.includes(role) || /^c\d+$/.test(role || '');

const emptyForm = {
  user_id: '',
  nama_lengkap: '',
  role: 'admin_prodi',
  prodi: '',
  is_active: true,
};

const emptyProdiForm = {
  kode_prodi: '',
  nama_prodi: '',
  jenjang: '',
};

function SuperadminDashboard() {
  const navigate = useNavigate();

  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [lookups, setLookups] = useState({ prodiList: [] });
  const [criteriaStats, setCriteriaStats] = useState([]);
  const [selectedProdiId, setSelectedProdiId] = useState('');
  const [roleOptions, setRoleOptions] = useState(BASE_ROLES);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [prodiForm, setProdiForm] = useState(emptyProdiForm);
  const [savingProdi, setSavingProdi] = useState(false);

  useEffect(() => {
    initPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initPage = async () => {
    setLoading(true);
    setErrorMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    setAuthUser(user);

    const { data: myProfile, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error || !myProfile) {
      setErrorMsg('Profile superadmin tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (myProfile.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }

    let lookupData;
    try {
      lookupData = await fetchProfileLookups(supabase);
      setLookups(lookupData);
    } catch (lookupError) {
      setErrorMsg(lookupError.message);
      setLoading(false);
      return;
    }

    setProfile(hydrateProfile(myProfile, lookupData));

    try {
      const scopedProdiId = selectedProdiId || lookupData.prodiList?.[0]?.id || '';
      if (scopedProdiId && !selectedProdiId) {
        setSelectedProdiId(scopedProdiId);
      }
      await Promise.all([
        fetchProfiles(lookupData),
        fetchRoleOptions(),
        fetchCriteriaStats(scopedProdiId),
      ]);
    } catch (loadError) {
      setErrorMsg(loadError.message);
    }

    setLoading(false);
  };

  const fetchProfiles = async (lookupData = lookups) => {
    const { data, error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Gagal mengambil profile: ${error.message}`);
    }

    setProfiles(hydrateProfiles(data || [], lookupData));
  };

  const fetchRoleOptions = async () => {
    const { data, error } = await supabase
      .schema('akreditasi')
      .from('kriteria')
      .select('kode_kriteria, urutan, is_active')
      .eq('is_active', true)
      .order('urutan', { ascending: true });

    if (error) {
      throw new Error(`Gagal mengambil role kriteria: ${error.message}`);
    }

    const criteriaRoles = (data || [])
      .map((item) => String(item.kode_kriteria || '').toLowerCase())
      .filter((role) => /^c\d+$/.test(role));

    setRoleOptions([...BASE_ROLES, ...criteriaRoles]);
  };

  const fetchCriteriaStats = async (prodiId = selectedProdiId) => {
    const dynamicCriteria = await fetchKriteriaList(supabase, {
      includeInactive: true,
      prodiId,
    });
    const criteriaConfig = mapCriteriaForAccess(dynamicCriteria).map((item) => ({
      code: item.kode_kriteria,
      name: item.nama_kriteria,
      route: prodiId ? `${item.route}?prodi=${prodiId}` : item.route,
      id: item.id,
      isActive: item.is_active,
    }));

    setCriteriaStats(criteriaConfig);
  };

  const visibleProfiles = useMemo(
    () => profiles.filter((item) => isManagedRole(item.role)),
    [profiles]
  );

  const activeCriteria = useMemo(
    () => criteriaStats.filter((item) => item.isActive),
    [criteriaStats]
  );

  const inactiveCriteria = useMemo(
    () => criteriaStats.filter((item) => !item.isActive),
    [criteriaStats]
  );

  const selectedProdi = useMemo(
    () => lookups.prodiList.find((item) => item.id === selectedProdiId) || null,
    [lookups.prodiList, selectedProdiId]
  );

  const roleDistribution = useMemo(
    () =>
      criteriaStats.map((criteria) => ({
        ...criteria,
        totalUser: visibleProfiles.filter(
          (item) => item.role === criteria.code.toLowerCase()
        ).length,
      })),
    [criteriaStats, visibleProfiles]
  );

  const emptyRoles = useMemo(
    () => roleDistribution.filter((item) => item.totalUser === 0),
    [roleDistribution]
  );

  const stats = [
    { title: 'Total Profile', value: profiles.length },
    { title: 'User Aktif', value: profiles.filter((p) => p.is_active).length },
    { title: 'Admin Prodi', value: profiles.filter((p) => p.role === 'admin_prodi').length },
    { title: 'Kriteria Aktif', value: activeCriteria.length },
  ];

  const resetForm = () => {
    setFormData(emptyForm);
    setEditId(null);
  };

  const handleShowAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setFormData({
      user_id: item.user_id || '',
      nama_lengkap: item.nama_lengkap || '',
      role: isManagedRole(item.role) ? item.role : 'admin_prodi',
      prodi: item.prodiData?.kode_prodi || item.prodi || '',
      is_active: item.is_active,
    });
    setEditId(item.id);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!formData.user_id || !formData.nama_lengkap || !formData.role) {
      setErrorMsg('User ID, nama lengkap, dan role wajib diisi.');
      return;
    }

    if (editId) {
      const { error } = await supabase
        .schema('akreditasi')
        .from('profiles')
        .update({ ...formData })
        .eq('id', editId);

      if (error) {
        setErrorMsg(error.message);
        return;
      }

      setSuccessMsg('Profile berhasil diperbarui.');
    } else {
      const { error } = await supabase
        .schema('akreditasi')
        .from('profiles')
        .insert([{ ...formData }]);

      if (error) {
        setErrorMsg(error.message);
        return;
      }

      setSuccessMsg('Profile berhasil ditambahkan.');
    }

    setShowModal(false);
    resetForm();
    await fetchProfiles();
  };

  const handleToggleActive = async (item) => {
    const { error } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .update({ is_active: !item.is_active })
      .eq('id', item.id);

    if (error) {
      setErrorMsg(error.message);
      return;
    }

    setSuccessMsg('Status user berhasil diperbarui.');
    await fetchProfiles();
  };

  const handleSaveProdi = async (e) => {
    e.preventDefault();
    setSavingProdi(true);
    setErrorMsg('');
    setSuccessMsg('');

    const payload = {
      kode_prodi: prodiForm.kode_prodi.trim().toUpperCase(),
      nama_prodi: prodiForm.nama_prodi.trim(),
      jenjang: prodiForm.jenjang.trim().toUpperCase(),
    };

    if (!payload.kode_prodi || !payload.nama_prodi || !payload.jenjang) {
      setErrorMsg('Kode prodi, nama prodi, dan jenjang wajib diisi.');
      setSavingProdi(false);
      return;
    }

    const { error } = await supabase
      .schema('akreditasi')
      .from('prodi')
      .insert([payload]);

    if (error) {
      setErrorMsg(error.message);
      setSavingProdi(false);
      return;
    }

    clearProfileLookupsCache();
    const nextLookups = await fetchProfileLookups(supabase, { force: true });
    setLookups(nextLookups);
    setProdiForm(emptyProdiForm);
    setSuccessMsg('Prodi baru berhasil ditambahkan.');
    setSavingProdi(false);
  };

  const handleProdiScopeChange = async (prodiId) => {
    setSelectedProdiId(prodiId);
    setLoading(true);
    try {
      await fetchCriteriaStats(prodiId);
    } catch (error) {
      setErrorMsg(error.message);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <DashboardLayout
        title="Dashboard Superadmin"
        subtitle="Memuat data superadmin..."
        profile={profile}
        activeMenu="superadmin"
      >
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Dashboard Superadmin"
      subtitle="Manajemen user dan monitoring sistem akreditasi Politeknik Negeri Manado"
      profile={profile}
      activeMenu="superadmin"
    >
      <Container fluid className="p-0">
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
        {successMsg && <Alert variant="success">{successMsg}</Alert>}

        <Card className="hero-card-v2 mb-3">
          <Card.Body>
            <div className="hero-eyebrow-v2">Selamat datang</div>
            <h2 className="hero-title-v2">{profile?.nama_lengkap || 'Superadmin'}</h2>
          </Card.Body>
        </Card>

        <Row className="g-3 mb-3">
          {stats.map((item) => (
            <Col xl={3} md={6} key={item.title}>
              <Card className="content-card-v2 h-100">
                <Card.Body>
                  <div className="section-subtitle-v2">{item.title}</div>
                  <div className="summary-number-v2">{item.value}</div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-3 mb-3">
          <Col xl={8}>
            <Card className="content-card-v2">
              <Card.Body>
                <div className="section-head-v2 mb-3">
                  <div>
                    <h4 className="section-title-v2">Struktur Kriteria Prodi</h4>
                  </div>
                  <div className="d-flex gap-2 flex-wrap justify-content-end">
                    <Form.Select
                      style={{ maxWidth: 260 }}
                      value={selectedProdiId}
                      onChange={(e) => handleProdiScopeChange(e.target.value)}
                    >
                      {lookups.prodiList.map((prodi) => (
                        <option value={prodi.id} key={prodi.id}>
                          {formatProdi(prodi)}
                        </option>
                      ))}
                    </Form.Select>
                    <Button variant="outline-primary" onClick={() => navigate('/settings/kriteria')}>
                      Kelola Kriteria
                    </Button>
                  </div>
                </div>

                <div className="superadmin-criteria-grid">
                  {criteriaStats.map((item) => (
                    <button
                      type="button"
                      className="superadmin-criteria-card"
                      key={item.code}
                      onClick={() => navigate(item.route)}
                    >
                      <div>
                        <strong>{item.code}</strong>
                        <span>{item.name}</span>
                      </div>
                      <Badge bg={item.isActive ? 'success' : 'secondary'}>
                        {item.isActive ? 'aktif' : 'nonaktif'}
                      </Badge>
                    </button>
                  ))}
                  {criteriaStats.length === 0 && (
                    <div className="criterion-softdelete-note">
                      Belum ada kriteria untuk {selectedProdi ? formatProdi(selectedProdi) : 'prodi ini'}.
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="content-card-v2">
              <Card.Body>
                <h4 className="section-title-v2 mb-3">Aksi Superadmin</h4>
                <div className="readiness-summary-grid">
                  <div className="readiness-summary-box ready">
                    <div className="readiness-summary-label">Aktif</div>
                    <div className="readiness-summary-value">
                      {activeCriteria.length} kriteria
                    </div>
                  </div>
                  <div className="readiness-summary-box attention">
                    <div className="readiness-summary-label">Nonaktif</div>
                    <div className="readiness-summary-value">
                      {inactiveCriteria.length} kriteria
                    </div>
                  </div>
                </div>

                <div className="superadmin-action-list">
                  <Button variant="primary" onClick={() => navigate('/settings/kriteria')}>
                    Kelola Kriteria
                  </Button>
                  <Button variant="outline-primary" onClick={() => navigate('/laporan')}>
                    Laporan
                  </Button>
                  <Button variant="outline-primary" onClick={() => navigate('/arsip')}>
                    Arsip Data
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xl={6}>
            <Card className="content-card-v2 h-100">
              <Card.Body>
                <h4 className="section-title-v2 mb-3">Distribusi Role Kriteria</h4>
                <div className="team-distribution-list">
                  {roleDistribution.map((item) => (
                    <div className="team-distribution-item" key={item.code}>
                      <span>
                        <strong>{item.code}</strong> - {item.name}
                      </span>
                      <Badge bg={item.totalUser > 0 ? 'primary' : 'secondary'}>
                        {item.totalUser} user
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6}>
            <Card className="content-card-v2 h-100">
              <Card.Body>
                <h4 className="section-title-v2 mb-3">Yang Perlu Perhatian</h4>
                <div className="report-attention-list">
                  {emptyRoles.map((item) => (
                    <div className="report-attention-item" key={`role-${item.code}`}>
                      <div>
                        <strong>Role {item.code.toLowerCase()}</strong>
                        <span>Belum memiliki user aktif di dashboard.</span>
                      </div>
                      <Badge bg="secondary">role kosong</Badge>
                    </div>
                  ))}

                  {emptyRoles.length === 0 && (
                    <div className="readiness-complete-note">
                      Tidak ada catatan perhatian utama saat ini.
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          <Col xl={8}>
            <Card className="content-card-v2">
              <Card.Body>
                <div className="section-head-v2 mb-3">
                  <div>
                    <h4 className="section-title-v2">Kelola Profile User</h4>
                  </div>

                  <Button className="save-btn-v2" onClick={handleShowAdd}>
                    Tambah Profile
                  </Button>
                </div>

                <Table responsive hover className="table-v2 align-middle">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>User UUID</th>
                      <th>Nama Lengkap</th>
                      <th>Role</th>
                      <th>Prodi</th>
                      <th>Status</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleProfiles.length > 0 ? (
                      visibleProfiles.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td className="uuid-cell">{item.user_id}</td>
                          <td>{item.nama_lengkap}</td>
                          <td>
                            <Badge bg="primary">{formatRoleLabel(item)}</Badge>
                          </td>
                          <td>{item.prodi_display || item.prodi || '-'}</td>
                          <td>
                            <Badge bg={item.is_active ? 'success' : 'secondary'}>
                              {item.is_active ? 'Aktif' : 'Nonaktif'}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleEdit(item)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant={item.is_active ? 'outline-danger' : 'outline-success'}
                                onClick={() => handleToggleActive(item)}
                              >
                                {item.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          Belum ada data profile.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="content-card-v2 mb-4">
              <Card.Body>
                <h4 className="section-title-v2 mb-3">Informasi Login</h4>
                <div className="info-box-v2 mb-3">
                  <div className="info-label-v2">Email Auth</div>
                  <div className="info-value-v2">{authUser?.email || '-'}</div>
                </div>
                <div className="info-box-v2">
                  <div className="info-label-v2">Nama Superadmin</div>
                  <div className="info-value-v2">{profile?.nama_lengkap || '-'}</div>
                </div>
              </Card.Body>
            </Card>

            <Card className="content-card-v2 mt-4">
              <Card.Body>
                <h4 className="section-title-v2 mb-3">Tambah Prodi</h4>
                <Form onSubmit={handleSaveProdi}>
                  <Form.Group className="mb-2">
                    <Form.Label>Kode Prodi</Form.Label>
                    <Form.Control
                      value={prodiForm.kode_prodi}
                      onChange={(e) =>
                        setProdiForm({ ...prodiForm, kode_prodi: e.target.value })
                      }
                      placeholder="Contoh: TI-D4"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Nama Prodi</Form.Label>
                    <Form.Control
                      value={prodiForm.nama_prodi}
                      onChange={(e) =>
                        setProdiForm({ ...prodiForm, nama_prodi: e.target.value })
                      }
                      placeholder="Contoh: Teknik Informatika"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Jenjang</Form.Label>
                    <Form.Control
                      value={prodiForm.jenjang}
                      onChange={(e) =>
                        setProdiForm({ ...prodiForm, jenjang: e.target.value })
                      }
                      placeholder="Contoh: D4"
                      required
                    />
                  </Form.Group>

                  <Button className="save-btn-v2 w-100" type="submit" disabled={savingProdi}>
                    {savingProdi ? 'Menyimpan...' : 'Tambah Prodi'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editId ? 'Edit Profile User' : 'Tambah Profile User'}</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Label>User UUID dari Authentication</Form.Label>
                <Form.Control
                  value={formData.user_id}
                  onChange={(e) =>
                    setFormData({ ...formData, user_id: e.target.value })
                  }
                  required
                />
              </Col>

              <Col md={6}>
                <Form.Label>Nama Lengkap</Form.Label>
                <Form.Control
                  value={formData.nama_lengkap}
                  onChange={(e) =>
                    setFormData({ ...formData, nama_lengkap: e.target.value })
                  }
                  required
                />
              </Col>

              <Col md={6}>
                <Form.Label>Role</Form.Label>
                <Form.Select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                >
                  {roleOptions.map((role) => (
                    <option value={role} key={role}>
                      {formatRoleLabel(role)}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6}>
                <Form.Label>Prodi</Form.Label>
                <Form.Select
                  value={formData.prodi}
                  onChange={(e) =>
                    setFormData({ ...formData, prodi: e.target.value })
                  }
                >
                  <option value="">Pilih Prodi</option>
                  {lookups.prodiList.map((item) => (
                    <option value={item.kode_prodi} key={item.id}>
                      {formatProdi(item)}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6}>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={String(formData.is_active)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      is_active: e.target.value === 'true',
                    })
                  }
                >
                  <option value="true">Aktif</option>
                  <option value="false">Nonaktif</option>
                </Form.Select>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Batal
            </Button>
            <Button className="save-btn-v2" type="submit">
              Simpan
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </DashboardLayout>
  );
}

export default SuperadminDashboard;
